/**
 * RichText — dependency-free inline Markdown renderer for bot-generated text.
 *
 * Supports:
 *   - **bold** → <strong>
 *   - blank-line paragraph breaks (\n\n+) → separate <p> elements
 *   - single \n within a paragraph → <br/>
 *
 * Does NOT use dangerouslySetInnerHTML.
 */

import React from 'react';
import styles from './RichText.module.css';

/** Remove **bold** / __bold__ markers and collapse newlines to spaces. */
export function stripMarkdown(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\n+/g, ' ')
    .trim();
}

/**
 * Parse a single line (no newlines) into an array of React nodes,
 * converting **bold** spans to <strong> elements.
 */
function parseLine(line: string, keyPrefix: string): React.ReactNode[] {
  const parts = line.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) {
      return <strong key={`${keyPrefix}-b${i}`}>{bold[1]}</strong>;
    }
    return part;
  });
}

/**
 * Parse a paragraph (which may contain single \n line breaks) into React nodes.
 */
function parseParagraph(para: string, paraKey: string): React.ReactNode {
  const lines = para.split('\n');
  const nodes: React.ReactNode[] = [];
  lines.forEach((line, li) => {
    const lineNodes = parseLine(line, `${paraKey}-l${li}`);
    nodes.push(...lineNodes);
    if (li < lines.length - 1) {
      nodes.push(<br key={`${paraKey}-br${li}`} />);
    }
  });
  return nodes;
}

interface RichTextProps {
  text: string;
  className?: string;
  'data-testid'?: string;
}

export function RichText({ text, className, 'data-testid': testId }: RichTextProps): JSX.Element {
  const paragraphs = text.split(/\n{2,}/);

  return (
    <div data-testid={testId}>
      {paragraphs.map((para, pi) => (
        <p key={pi} className={[styles.paragraph, className].filter(Boolean).join(' ')}>
          {parseParagraph(para, `p${pi}`)}
        </p>
      ))}
    </div>
  );
}
